import React, { useState, useEffect, useCallback } from 'react';
import {
  LicenseInfo,
  fetchLicenseInfo,
  applyLicenseKey,
  clearLicense,
  isTrialActive,
  getStoredUserEmail,
} from '../services/LicenseService';
import { getLicensingPortalUrl, LICENSING_PORTAL_URL, LICENSING_SERVER_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';

// Display the portal host (e.g., "licensing.cavuinnovations.com" in prod,
// "qa-licensing.cavuinnovations.com" in QA) so users see which environment
// they're being directed to.
const LICENSING_PORTAL_HOST = new URL(LICENSING_PORTAL_URL).hostname;

// Constants
const NO_LICENSE_TIER = 'NO_LICENSE';

interface LicenseManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onRequestTrial?: () => void;  // Callback to open TrialRegistrationDialog
}

/**
 * Unified sign-in + license dialog.
 *
 * One dialog, three states, chosen automatically from license presence and
 * auth status:
 *
 *  1. No license, not signed in  → license key + email + password in one form.
 *     The Sign In button orchestrates the chain: apply the key, then direct
 *     login — the services stay separate; the UI guides the order.
 *  2. License present, not signed in → license summary + email + password.
 *     "Use a different license" reveals a replacement key field (applied as
 *     part of Sign In) and the clear-license action.
 *  3. Signed in → full license management: usage, details, trial info,
 *     apply/upgrade key, clear license.
 *
 * Replaces the old two-surface flow (license dialog first, then a separate
 * OAuth2 redirect to LS's hosted login page).
 */
export const LicenseManagementDialog: React.FC<LicenseManagementDialogProps> = ({ open, onClose, onRequestTrial }) => {
  const { state: authState, directLogin } = useAuth();
  const isAuthenticated = authState.isAuthenticated;

  const [licenseKey, setLicenseKey] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);          // management-view apply
  const [signingIn, setSigningIn] = useState(false);      // sign-in orchestration
  const [error, setError] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentLicense, setCurrentLicense] = useState<LicenseInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [clearingLicense, setClearingLicense] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showReplaceLicense, setShowReplaceLicense] = useState(false);
  const [revealKey, setRevealKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasLicense = !!currentLicense && currentLicense.tier !== NO_LICENSE_TIER;

  const loadLicenseInfo = useCallback(async () => {
    setLoadingInfo(true);
    try {
      const data = await fetchLicenseInfo();
      setCurrentLicense(data);
    } catch (err) {
      console.error('Failed to fetch license info', err);
    } finally {
      setLoadingInfo(false);
    }
  }, []);

  // Load current license information whenever the dialog opens; prefill the
  // email from the last signed-in user for the sign-in views.
  useEffect(() => {
    if (open) {
      loadLicenseInfo();
      setError(null);
      setSuccess(null);
      setShowReplaceLicense(false);
      const storedEmail = getStoredUserEmail();
      if (storedEmail) {
        setEmail((prev) => prev || storedEmail);
      }
    } else {
      // Never retain credentials after the dialog closes.
      setPassword('');
    }
  }, [open, loadLicenseInfo]);

  /** Map an apply-license failure to the user-facing error message. */
  const applyErrorMessage = (data: { error?: string; message?: string }): string => {
    const errorMsg = data.error || 'Failed to apply license key';
    if (errorMsg.startsWith('LICENSE_EXPIRED')) {
      return (
        'Trial Ended\n\n' +
        'Your trial period has ended and this license key is no longer active. ' +
        'This trial cannot be cancelled.\n\n' +
        'If your trial converted to a paid subscription, a new license key was sent to your email. ' +
        'Please check your inbox and enter the new license key.\n\n' +
        'For more information about your licenses, visit the Customer Portal at ' +
        getLicensingPortalUrl('/customer/login') + '\n\n' +
        'If you need assistance, please contact support@cavuinnovations.com.'
      );
    }
    const detailMsg = data.message || '';
    return detailMsg && detailMsg !== errorMsg
      ? `${errorMsg}\n\nDetails: ${detailMsg}`
      : (detailMsg || errorMsg);
  };

  /**
   * Sign-in orchestration (states 1 and 2): with all the information entered
   * in one form, run the service calls in order — apply the license key (when
   * one was entered), then verify credentials and establish the session.
   */
  const handleSignIn = async () => {
    setError(null);
    setSuccess(null);
    setResendStatus('idle');
    setResendMessage(null);

    const needsKey = !hasLicense || showReplaceLicense;
    const keyEntered = licenseKey.trim().length > 0;

    if (needsKey && !keyEntered && !hasLicense) {
      setError('Please enter a license key');
      return;
    }
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setSigningIn(true);
    try {
      // Stage 1 — license (only when a new key was entered)
      if (keyEntered) {
        const applied = await applyLicenseKey(licenseKey);
        if (!applied.success) {
          setError(applyErrorMessage(applied));
          return;
        }
        setLicenseKey('');
        setShowReplaceLicense(false);
        await loadLicenseInfo();
      }

      // Stage 2 — credentials + session (server verifies license ownership)
      const result = await directLogin(email.trim(), password);
      if (!result.success) {
        setError(result.message || 'Sign-in failed');
        return;
      }

      setPassword('');
      onClose();
    } finally {
      setSigningIn(false);
    }
  };

  /** Management-view apply (state 3 — already signed in). */
  const handleApplyLicense = async () => {
    if (!licenseKey.trim()) {
      setError('Please enter a license key');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await applyLicenseKey(licenseKey);

      if (data.success) {
        setSuccess(`License activated — you're on the ${data.newTier} plan.`);
        setLicenseKey('');
        // Refresh local license info (applyLicenseKey already dispatches `license-changed`,
        // which the LicenseContext picks up to refresh other consumers).
        await loadLicenseInfo();
      } else {
        setError(applyErrorMessage(data));
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearLicense = async () => {
    if (!showClearConfirm) {
      setShowClearConfirm(true);
      return;
    }

    setClearingLicense(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await clearLicense();
      if (result.success) {
        setSuccess(`License cleared successfully!\nCleared from ${result.previousTier}. Apply a new license key or start a trial to continue.`);
        setShowClearConfirm(false);
        setShowReplaceLicense(false);
        // Refresh local license info (clearLicense already dispatches `license-changed`,
        // which the LicenseContext picks up to refresh other consumers).
        await loadLicenseInfo();
      } else {
        setError(result.error || result.message || 'Failed to clear license');
      }
    } catch (err) {
      setError(`Failed to clear license: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setClearingLicense(false);
    }
  };

  // Check if trial is active
  const trialActive = isTrialActive(currentLicense);

  const getTierBadgeColor = (tier?: string | null) => {
    if (!tier) return 'bg-gray-500';

    // 4-tier model: Trial / Professional / Business / Enterprise
    switch (tier.toUpperCase()) {
      case 'TRIAL':
        return 'bg-green-500';
      case 'PROFESSIONAL':
        return 'bg-purple-600';
      case 'BUSINESS':
        return 'bg-blue-500';
      case 'ENTERPRISE':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
      default:
        return 'bg-gray-500';
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Mask all but the last 4 characters of the license key. Pattern matches
  // how secret-bearing UIs (AWS, Stripe, GitHub tokens) surface long-lived
  // credentials: visible enough for the owner to recognize, not exposed for
  // shoulder-surfing / screen-sharing / screenshots.
  const maskLicenseKey = (key: string): string => {
    if (key.length <= 4) return '••••';
    return '•'.repeat(Math.max(8, key.length - 4)) + key.slice(-4);
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.warn('Clipboard write failed:', e);
    }
  };

  if (!open) return null;

  // LS refuses sign-in for an unverified account with a message that starts
  // "Please verify your email address" (CustomerAuthService). That is not a
  // failure the user can fix by retyping — give it its own panel with the
  // steps and a resend button instead of the generic red box.
  const isUnverified = !!error && /verify your email/i.test(error);
  const handleResendVerification = async () => {
    const target = email.trim();
    if (!target) {
      setResendStatus('failed');
      setResendMessage('Enter your email address above first.');
      return;
    }
    setResendStatus('sending');
    setResendMessage(null);
    try {
      const response = await fetch(`${LICENSING_SERVER_URL}/api/customer/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: target }),
      });
      const data = await response.json().catch(() => ({} as { success?: boolean; message?: string }));
      if (response.ok && data.success !== false) {
        setResendStatus('sent');
        setResendMessage(data.message || `Verification email sent again to ${target}.`);
      } else {
        setResendStatus('failed');
        setResendMessage(data.message || 'Could not resend right now. Please try again in a minute.');
      }
    } catch {
      setResendStatus('failed');
      setResendMessage('Could not reach the licensing server. Please try again in a minute.');
    }
  };
  const unverifiedSection = isUnverified && (
    <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <svg className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <div>
          <h4 className="font-bold text-amber-900">Verify your email first</h4>
          <p className="text-sm text-amber-900 mt-1">
            Your account exists but the email address hasn&rsquo;t been verified yet, so sign-in is refused.
            Open the verification email we sent to <strong className="break-all">{email.trim() || 'your address'}</strong>
            {' '}(check Spam / Junk), click <strong>Verify</strong>, then sign in again here.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendStatus === 'sending'}
              className="px-3 py-1.5 rounded-md border border-amber-500 text-amber-900 font-medium hover:bg-amber-100 disabled:opacity-60"
            >
              {resendStatus === 'sending' ? 'Sending…' : 'Resend verification email'}
            </button>
            {resendMessage && (
              <span className={resendStatus === 'failed' ? 'text-red-700' : 'text-green-800'}>{resendMessage}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Shared error box — amber styling for the Trial Ended special case.
  const errorSection = error && !isUnverified && (
    <div className={`border rounded-lg p-4 ${
      error.startsWith('Trial Ended')
        ? 'bg-amber-50 border-amber-300'
        : 'bg-red-50 border-red-200'
    }`}>
      <div className="flex items-start">
        <svg className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
          error.startsWith('Trial Ended') ? 'text-amber-600' : 'text-red-600'
        }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <h4 className={`font-semibold mb-1 ${
            error.startsWith('Trial Ended') ? 'text-amber-900' : 'text-red-900'
          }`}>
            {error.startsWith('Trial Ended') ? 'Trial Ended' : 'Sign-In Failed'}
          </h4>
          <p className={`text-sm whitespace-pre-line ${
            error.startsWith('Trial Ended') ? 'text-amber-700' : 'text-red-700'
          }`}>
            {error.startsWith('Trial Ended') ? error.substring('Trial Ended\n\n'.length) : error}
          </p>
        </div>
      </div>
    </div>
  );

  const successSection = success && (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <div className="flex items-start">
        <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="flex-1">
          <p className="text-sm text-green-700 whitespace-pre-line">{success}</p>
        </div>
      </div>
    </div>
  );

  const helpSection = (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Need a License?
      </h4>
      <p className="text-sm text-blue-700">
        Visit <a href={getLicensingPortalUrl('/')} target="_blank" rel="noopener noreferrer" className="underline font-semibold">{LICENSING_PORTAL_HOST}</a> to sign up or start a free 7-day trial.
        For offline activation or support, contact <a href="mailto:support@cavuinnovations.com" className="underline">support@cavuinnovations.com</a>
      </p>
    </div>
  );

  const clearLicenseSection = hasLicense && (
    <div className="border-t border-gray-200 pt-6">
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Clear License</h3>
        <p className="text-sm text-gray-600">
          Clearing your license will remove it from this application. You'll need to apply a new license key or start a trial to continue.
          Your license will remain valid and can be applied to another installation.
        </p>
      </div>
      {!showClearConfirm ? (
        <button
          onClick={() => setShowClearConfirm(true)}
          className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Clear License
        </button>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2 mb-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h4 className="font-semibold text-red-900 mb-1">Confirm License Removal</h4>
              <p className="text-sm text-red-700">
                Are you sure you want to clear this license? You'll lose access until you apply a new license key or start a trial.
                {trialActive && (
                  <span className="block mt-2 font-medium">
                    Note: Clearing a trial license does not cancel the trial subscription. Visit the Licensing Server Customer Portal to cancel your trial.
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowClearConfirm(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleClearLicense}
              disabled={clearingLicense}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
            >
              {clearingLicense ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Clearing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Yes, Clear License</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const licenseKeyInput = (
    <div>
      <label htmlFor="licenseKey" className="block text-sm font-medium text-gray-700 mb-2">
        License Key <span className="text-red-500">*</span>
      </label>
      <input
        id="licenseKey"
        type="text"
        value={licenseKey}
        onChange={(e) => setLicenseKey(e.target.value)}
        placeholder="XXXX-XXXX-XXXX-XXXX"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-wider"
        disabled={signingIn}
      />
      <p className="mt-2 text-sm text-gray-500">
        Enter your license key from CAVU Innovations.
      </p>
    </div>
  );

  const credentialInputs = (
    <>
      <div>
        <label htmlFor="signInEmail" className="block text-sm font-medium text-gray-700 mb-2">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="signInEmail"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={signingIn}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="signInPassword" className="block text-sm font-medium text-gray-700">
            Password <span className="text-red-500">*</span>
          </label>
          <a
            href={getLicensingPortalUrl('/customer/forgot-password')}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Forgot password?
          </a>
        </div>
        <input
          id="signInPassword"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !signingIn) handleSignIn(); }}
          placeholder="Your password"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={signingIn}
        />
      </div>
    </>
  );

  const signInButton = (
    <button
      onClick={handleSignIn}
      disabled={signingIn}
      className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
        signingIn
          ? 'bg-gray-400 cursor-not-allowed'
          : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
      }`}
    >
      {signingIn ? (
        <span className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Signing In...
        </span>
      ) : (
        'Sign In'
      )}
    </button>
  );

  // ---- Sign-in views (states 1 and 2: not authenticated) ----

  const activeLicenseSummary = hasLicense && currentLicense && (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-gray-900">Active License</h3>
        <span className={`px-3 py-1 rounded-full text-white font-semibold text-xs ${getTierBadgeColor(currentLicense.tier)}`}>
          {currentLicense.tier}
        </span>
      </div>
      <div className="text-sm text-gray-600 space-y-1">
        {currentLicense.customerName && (
          <div className="flex justify-between gap-3">
            <span>Customer:</span>
            <span className="font-medium text-gray-900 truncate">{currentLicense.customerName}</span>
          </div>
        )}
        {currentLicense.expiresAt && (
          <div className="flex justify-between gap-3">
            <span>Expires:</span>
            <span className="font-medium text-gray-900">{new Date(currentLicense.expiresAt).toLocaleDateString()}</span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => setShowReplaceLicense(v => !v)}
        className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
        disabled={signingIn}
      >
        {showReplaceLicense ? 'Keep this license' : 'Use a different license'}
      </button>
    </div>
  );

  const signInView = (
    <div className="space-y-4">
      {hasLicense ? (
        <>
          {activeLicenseSummary}
          {showReplaceLicense && (
            <>
              {licenseKeyInput}
              {clearLicenseSection}
            </>
          )}
        </>
      ) : (
        licenseKeyInput
      )}

      {credentialInputs}
      {unverifiedSection}
      {errorSection}
      {successSection}
      {signInButton}

      {onRequestTrial && (
        <div className="pt-2">
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
            <div className="relative flex justify-center text-xs text-gray-500"><span className="bg-white px-3">New to PDF Form Builder?</span></div>
          </div>
          <button
            onClick={() => {
              onClose();
              onRequestTrial();
            }}
            disabled={signingIn}
            className="w-full py-3 px-4 bg-white border border-blue-300 text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Start Your Free 7-Day Trial
          </button>
        </div>
      )}

      {helpSection}
    </div>
  );

  // ---- Management view (state 3: authenticated) ----

  // Extracted so it can render before the "No Active License" card for
  // no-license users (so they can act immediately) while still rendering
  // after the "Current License" card for users who already have one.
  const applyLicenseSection = (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Apply New License Key</h3>

      <div className="space-y-4">
        <div>
          <label htmlFor="licenseKey" className="block text-sm font-medium text-gray-700 mb-2">
            Enter License Key
          </label>
          <input
            id="licenseKey"
            type="text"
            value={licenseKey}
            onChange={(e) => setLicenseKey(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleApplyLicense()}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-wider"
            disabled={loading}
          />
          <p className="mt-2 text-sm text-gray-500">
            Enter your license key from CAVU Innovations to activate or upgrade your tier.
          </p>
        </div>

        {errorSection}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h4 className="font-semibold text-green-900 mb-1">License Activated!</h4>
                <p className="text-sm text-green-700 whitespace-pre-line">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Apply Button */}
        <button
          onClick={handleApplyLicense}
          disabled={loading || !licenseKey.trim()}
          className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
            loading || !licenseKey.trim()
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Validating License...
            </span>
          ) : (
            'Apply License Key'
          )}
        </button>
      </div>
    </div>
  );

  const managementView = (
    <>
      {currentLicense && currentLicense.tier !== NO_LICENSE_TIER ? (
        <>
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Current License</h3>
            <span className={`px-4 py-1 rounded-full text-white font-semibold text-sm ${getTierBadgeColor(currentLicense.tier)}`}>
              {currentLicense.tier}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* PDF Uploads */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <h4 className="font-semibold text-gray-700">PDF Uploads</h4>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Used:</span>
                  <span className="font-semibold text-gray-900">
                    {currentLicense.pdfUploads.current} / {formatNumber(currentLicense.pdfUploads.limit)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{
                      width: currentLicense.pdfUploads.limit === -1
                        ? '100%'
                        : `${Math.min(100, (currentLicense.pdfUploads.current / currentLicense.pdfUploads.limit) * 100)}%`
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Code Generation */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                <h4 className="font-semibold text-gray-700">Code Generation</h4>
              </div>
              <div className="flex items-center justify-center h-8">
                {currentLicense.codeGeneration.enabled ? (
                  <span className="flex items-center text-green-600 font-semibold">
                    <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Enabled
                  </span>
                ) : (
                  <span className="flex items-center text-red-600 font-semibold">
                    <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Disabled
                  </span>
                )}
              </div>
            </div>

            {/* Flattened PDFs */}
            <div className="bg-white rounded-lg p-4 shadow-sm col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h4 className="font-semibold text-gray-700">Flattened PDFs</h4>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Used:</span>
                  <span className="font-semibold text-gray-900">
                    {currentLicense.flattenedPdfs.current} / {formatNumber(currentLicense.flattenedPdfs.limit)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-amber-500 h-2 rounded-full transition-all"
                    style={{
                      width: currentLicense.flattenedPdfs.limit === -1
                        ? '100%'
                        : `${Math.min(100, (currentLicense.flattenedPdfs.current / currentLicense.flattenedPdfs.limit) * 100)}%`
                    }}
                  />
                </div>
                {(currentLicense.flattenedPdfs.allocated ?? 0) > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {currentLicense.flattenedPdfs.allocated?.toLocaleString()} allocated to generated projects
                  </p>
                )}
              </div>
            </div>

            {/* Period Info */}
            <div className="bg-white rounded-lg p-4 shadow-sm col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h4 className="font-semibold text-gray-700">Billing Period</h4>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-sm">
                  <span className="text-gray-600">Period Type: </span>
                  <span className="font-semibold text-gray-900">{currentLicense.periodType}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">Period Resets: </span>
                  <span className="font-semibold text-gray-900">
                    {(() => {
                      // Parse date-only string as local date to avoid timezone shift
                      const dateStr = currentLicense.currentPeriod.resetsOn;
                      const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
                      return new Date(year, month - 1, day).toLocaleDateString();
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {/* License Details — customer, expiration, masked key */}
            {(currentLicense.customerName || currentLicense.expiresAt || currentLicense.licenseKey) && (
              <div className="bg-white rounded-lg p-4 shadow-sm col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <h4 className="font-semibold text-gray-700">License Details</h4>
                </div>
                <div className="space-y-2 text-sm">
                  {currentLicense.customerName && (
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-gray-600 flex-shrink-0">Customer:</span>
                      <span className="font-semibold text-gray-900 truncate" title={currentLicense.customerName}>
                        {currentLicense.customerName}
                      </span>
                    </div>
                  )}
                  {currentLicense.expiresAt && (
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-gray-600 flex-shrink-0">Expires:</span>
                      <span className="font-semibold text-gray-900">
                        {new Date(currentLicense.expiresAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {currentLicense.licenseKey && (
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-gray-600 flex-shrink-0">License Key:</span>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs text-gray-900 truncate" title={revealKey ? currentLicense.licenseKey : 'Hidden'}>
                          {revealKey ? currentLicense.licenseKey : maskLicenseKey(currentLicense.licenseKey)}
                        </span>
                        <button
                          type="button"
                          onClick={() => setRevealKey(v => !v)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
                          title={revealKey ? 'Hide the license key' : 'Reveal the license key'}
                        >
                          {revealKey ? 'Hide' : 'Show'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyKey(currentLicense.licenseKey!)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
                          title="Copy license key to clipboard"
                        >
                          {copied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Trial Information */}
          {currentLicense.trial && currentLicense.trial.hasTrial && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="font-bold text-yellow-900 text-lg">Trial Subscription Active</h4>
                  {currentLicense.trial.status === 'PENDING' && (
                    <span className="ml-auto px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full border border-yellow-300">
                      TRIAL
                    </span>
                  )}
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-yellow-800 font-medium">Selected Tier:</span>
                    <span className="text-sm font-bold text-yellow-900">{currentLicense.trial.selectedTier}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-yellow-800 font-medium">Payment Amount:</span>
                    <span className="text-sm font-bold text-yellow-900">{currentLicense.trial.priceFormatted}</span>
                  </div>
                  {currentLicense.trial.trialEndDate && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-yellow-800 font-medium">Trial Ends:</span>
                      <span className={`text-sm font-bold ${currentLicense.trial.daysRemaining <= 2 ? 'text-red-600' : 'text-yellow-900'}`}>
                        {(() => {
                          // Parse date-only string as local date to avoid timezone shift
                          const dateStr = currentLicense.trial.trialEndDate;
                          const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
                          return new Date(year, month - 1, day).toLocaleDateString();
                        })()}
                      </span>
                    </div>
                  )}
                </div>

                {currentLicense.trial.message && (
                  <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-900 font-medium">{currentLicense.trial.message}</p>
                  </div>
                )}

                {/* Trial Cancellation Link */}
                {currentLicense.trial.canCancel && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-1">Cancel Your Trial</h4>
                        <p className="text-sm text-blue-800 mb-3">
                          To cancel your trial subscription, visit the CAVU Licensing Server Customer Portal.
                          <strong className="block mt-1">The Licensing Server will verify that you have proper Customer Admin authorization before allowing trial cancellation.</strong>
                          <span className="block mt-1 text-xs">Only Customer Admin accounts are authorized to cancel trial subscriptions for security and billing protection.</span>
                        </p>
                        <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mb-3">
                          <p className="text-xs text-blue-900">
                            <strong>Billing Schedule:</strong> Your payment will be charged when your 7-day trial ends. You can cancel anytime before the trial end date to avoid charges.
                          </p>
                        </div>
                        <a
                          href={getLicensingPortalUrl('/customer/login')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <span>Go to Licensing Server Portal</span>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {applyLicenseSection}
        </>
      ) : currentLicense && currentLicense.tier === NO_LICENSE_TIER ? (
        <>
        {applyLicenseSection}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 border border-amber-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">No Active License</h3>
            <span className="px-4 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold text-sm border border-amber-300">
              NO LICENSE
            </span>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-gray-700 font-medium mb-2">You don't have an active license yet.</p>
                <p className="text-sm text-gray-600">
                  To start using PDF Form Builder, request a free 7-day trial below.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h4 className="font-semibold text-gray-900 mb-3">Get Started:</h4>

              {/* Request Trial Button */}
              {onRequestTrial && (
                <button
                  onClick={() => {
                    onClose();
                    onRequestTrial();
                  }}
                  className="w-full mb-4 py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-4 focus:ring-blue-200 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Request 7-Day Free Trial
                </button>
              )}

              {/* Alternative: Visit Licensing Server */}
              <div className="text-sm text-gray-600 mb-3">
                <p className="font-medium mb-2">Or visit the licensing server:</p>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">1</span>
                    <span>Visit <a href={getLicensingPortalUrl('/')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-medium">{LICENSING_PORTAL_HOST}</a></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">2</span>
                    <span>Access your existing license or purchase a new one</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">3</span>
                    <span>Enter your license key above to activate</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        </>
      ) : null}

      {clearLicenseSection}
      {helpSection}
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isAuthenticated ? 'License Management' : 'Sign In'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loadingInfo ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading license information...</p>
            </div>
          ) : isAuthenticated ? (
            managementView
          ) : (
            signInView
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
